import React, { useState, useEffect } from 'react';
import {
  pageContainer, cardStyle, primaryBtn, secondaryBtn,
  sectionTitle
} from '../styles/theme';
import { useToast } from '../utils/useAdminPage';
import Toast from '../components/Toast';
import PageHeader from '../components/PageHeader';
import NotificationPreferences from '../components/NotificationPreferences';
import SystemStatusDetailed from '../components/SystemStatusDetailed';
import RecentChanges from '../components/RecentChanges';
import PaymentGateway from '../components/PaymentGateway';
import API from '../services/api';
import { isSuperAdmin } from '../utils/currentUser';

// ── Config items for the General tab ─────────────────────────────────
const CONFIG_ITEMS = [
  // General Tab
  { key: 'platform_commission_rate', label: 'Commission Rate (%)', desc: 'Platform fee charged on each booking', category: 'finance' },
  { key: 'platform_max_booking_duration', label: 'Max Booking Duration (min)', desc: 'Maximum duration for a single booking', category: 'booking' },
  { key: 'platform_min_advance_booking_hours', label: 'Min Advance Booking (hrs)', desc: 'Minimum hours before scheduled time', category: 'booking' },
  { key: 'platform_max_active_bookings', label: 'Max Active Bookings per User', desc: 'Simultaneous active bookings allowed', category: 'booking' },
  { key: 'platform_support_email', label: 'Support Email', desc: 'Email shown to users for support', category: 'contact' },
  { key: 'platform_support_phone', label: 'Support Phone', desc: 'Phone number shown for support', category: 'contact' },
  { key: 'platform_maintenance_mode', label: 'Maintenance Mode', desc: 'Set to "true" to enable maintenance mode', category: 'system' },
  { key: 'platform_min_payout_amount', label: 'Min Payout Threshold (₹)', desc: 'Minimum amount required for scholar payout', category: 'finance' },
  
  // Payment Tab
  { key: 'payment_razorpay_key_id', label: 'Razorpay Key ID', desc: 'Used for frontend integration', category: 'payment' },
  { key: 'payment_razorpay_key_secret', label: 'Razorpay Key Secret', desc: 'Used for backend API calls', category: 'payment' },
  { key: 'payment_razorpay_webhook_secret', label: 'Webhook Secret', desc: 'Used to verify Razorpay webhooks', category: 'payment' },
  { key: 'payment_razorpay_fee_percentage', label: 'Processing Fee (%)', desc: 'Estimated fee for calculations', category: 'payment' },
  { key: 'payment_schedule', label: 'Payout Schedule', desc: 'Default payout frequency', category: 'payment' },
  
  // Notifications Tab
  { key: 'notifications_email', label: 'Email Notifications', desc: '', category: 'notifications' },
  { key: 'notifications_sms', label: 'SMS Notifications', desc: '', category: 'notifications' },
  { key: 'notifications_push', label: 'Push Notifications', desc: '', category: 'notifications' },

  // Support Channels
  { key: 'support_whatsapp_enabled', label: 'WhatsApp Support Enabled', desc: 'Enable/disable WhatsApp support channel', category: 'support_channels' },
  { key: 'support_whatsapp_number', label: 'WhatsApp Number', desc: 'WhatsApp support contact details', category: 'support_channels' },
  { key: 'support_phone_enabled', label: 'Phone Support Enabled', desc: 'Enable/disable Phone support channel', category: 'support_channels' },
  { key: 'support_phone_number', label: 'Phone Number', desc: 'Phone support contact details', category: 'support_channels' },
];

// ── Tab definitions ──────────────────────────────────────────────────
const TABS = [
  { id: 'general',       label: '️ General',      desc: 'Platform-wide configuration' },
  { id: 'payment',       label: ' Payment',       desc: 'Payment gateway settings' },
  { id: 'notifications', label: ' Notifications', desc: 'Notification preferences' },
  { id: 'system',        label: '️ System',        desc: 'System health & audit log' },
];

// ── Inputs styled with theme variables ───────────────────────────────
const inputStyle: React.CSSProperties = {
  padding: '10px 14px',
  borderRadius: 10,
  border: '1px solid hsl(var(--border))',
  background: 'hsl(var(--card))',
  color: 'hsl(var(--foreground))',
  fontSize: 14,
  width: '100%',
  boxSizing: 'border-box',
  outline: 'none',
  transition: 'border 0.15s',
};

const ConfigField: React.FC<{
  item: typeof CONFIG_ITEMS[0];
  value: string;
  onChange: (key: string, val: string) => void;
  disabled?: boolean;
}> = ({ item, value, onChange, disabled }) => (
  <div>
    <label style={{ display: 'block', marginBottom: 4, fontSize: 13, fontWeight: 600, color: 'hsl(var(--foreground))' }}>
      {item.label}
    </label>
    <div style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))', marginBottom: 6 }}>{item.desc}</div>
    <input
      type="text"
      value={value}
      onChange={e => onChange(item.key, e.target.value)}
      style={{ ...inputStyle, cursor: disabled ? 'default' : 'text', background: disabled ? 'hsl(var(--muted)/0.3)' : 'hsl(var(--card))' }}
      disabled={disabled}
    />
  </div>
);

// ── Main Settings Page ────────────────────────────────────────────────
const Settings = () => {
  const [activeTab, setActiveTab] = useState('general');
  const [configValues, setConfigValues] = useState<Record<string, string>>({});
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSupportChannelsModal, setShowSupportChannelsModal] = useState(false);
  const { toast, showToast } = useToast();
  const superAdmin = isSuperAdmin();

  // Load platform config
  useEffect(() => {
    const loadConfig = async () => {
      try {
        setLoadingConfig(true);
        const res = await API.get('/admin/config');
        const items: Array<{ key: string; value: string }> = res.data?.data || [];
        const map: Record<string, string> = {};
        items.forEach(c => { map[c.key] = c.value; });

        if (map['support_whatsapp_enabled'] === undefined) map['support_whatsapp_enabled'] = 'true';
        if (map['support_whatsapp_number'] === undefined) map['support_whatsapp_number'] = '+91 18001234567';
        if (map['support_phone_enabled'] === undefined) map['support_phone_enabled'] = 'true';
        if (map['support_phone_number'] === undefined) map['support_phone_number'] = '+91 9916877128';

        setConfigValues(map);
      } catch {
        // If not yet configured, start with empty defaults
        const defaults: Record<string, string> = {};
        CONFIG_ITEMS.forEach(i => { defaults[i.key] = ''; });
        defaults['support_whatsapp_enabled'] = 'true';
        defaults['support_whatsapp_number'] = '+91 18001234567';
        defaults['support_phone_enabled'] = 'true';
        defaults['support_phone_number'] = '+91 9916877128';
        setConfigValues(defaults);
      } finally {
        setLoadingConfig(false);
      }
    };
    loadConfig();
  }, []);

  const handleConfigChange = (key: string, value: string) => {
    setConfigValues(prev => ({ ...prev, [key]: value }));
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      const configs = Object.entries(configValues)
        .map(([key, value]) => ({ key, value, category: CONFIG_ITEMS.find(c => c.key === key)?.category || 'general' }));

      await API.put('/admin/config', { configs });
      showToast('Settings saved successfully');
    } catch {
      showToast('Failed to save settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleExportConfig = async () => {
    setSaving(true);
    try {
      const res = await API.post('/admin/config/backup');
      const blob = new Blob([JSON.stringify(res.data?.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `divinifyy-config-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      showToast('Config exported');
    } catch {
      showToast('Export failed', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={pageContainer}>
      <Toast toast={toast} />
      <PageHeader
        breadcrumb="Dashboard > Settings"
        title="Platform Settings"
        subtitle="Configure platform-wide settings, payments, notifications, and system parameters."
      />

      {/* ── Tab Navigation ── */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: 'hsl(var(--card))', padding: 6, borderRadius: 14, border: '1px solid hsl(var(--border))', width: 'fit-content' }}>
        {TABS.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '10px 20px',
              borderRadius: 10,
              border: 'none',
              cursor: 'pointer',
              fontWeight: 700,
              fontSize: 13,
              background: activeTab === tab.id ? 'hsl(var(--primary))' : 'transparent',
              color: activeTab === tab.id ? '#fff' : 'hsl(var(--muted-foreground))',
              transition: 'all 0.2s',
              boxShadow: activeTab === tab.id ? '0 3px 10px hsl(var(--primary)/0.25)' : 'none',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Tab: General ── */}
      {activeTab === 'general' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 24 }}>
          {/* Main config form */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

            {/* Finance settings group */}
            <div style={cardStyle}>
              <h3 style={{ ...sectionTitle, marginBottom: 20 }}>Finance & Commission</h3>
              {loadingConfig ? (
                <div style={{ color: 'hsl(var(--muted-foreground))', fontSize: 14 }}>Loading configuration…</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                  {CONFIG_ITEMS.filter(c => c.category === 'finance').map(item => (
                    <ConfigField key={item.key} item={item} value={configValues[item.key] || ''} onChange={handleConfigChange} disabled={!superAdmin} />
                  ))}
                </div>
              )}
            </div>

            {/* Booking settings group */}
            <div style={cardStyle}>
              <h3 style={{ ...sectionTitle, marginBottom: 20 }}>Booking Rules</h3>
              {loadingConfig ? (
                <div style={{ color: 'hsl(var(--muted-foreground))', fontSize: 14 }}>Loading configuration…</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                  {CONFIG_ITEMS.filter(c => c.category === 'booking').map(item => (
                    <ConfigField key={item.key} item={item} value={configValues[item.key] || ''} onChange={handleConfigChange} disabled={!superAdmin} />
                  ))}
                </div>
              )}
            </div>

            {/* Contact & System group */}
            <div style={cardStyle}>
              <h3 style={{ ...sectionTitle, marginBottom: 20 }}>Contact & System</h3>
              {loadingConfig ? (
                <div style={{ color: 'hsl(var(--muted-foreground))', fontSize: 14 }}>Loading configuration…</div>
              ) : (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                  {CONFIG_ITEMS.filter(c => c.category === 'contact' || c.category === 'system').map(item => (
                    <ConfigField key={item.key} item={item} value={configValues[item.key] || ''} onChange={handleConfigChange} disabled={!superAdmin} />
                  ))}
                </div>
              )}
            </div>

            {/* Scholar Support Channels group */}
            <div style={cardStyle}>
              <h3 style={{ ...sectionTitle, marginBottom: 14 }}>Scholar Support Channels</h3>
              <p style={{ fontSize: 13, color: 'hsl(var(--muted-foreground))', marginBottom: 16 }}>
                Control which support contact channels are shown to Scholars and update their respective phone numbers.
              </p>
              <button 
                onClick={() => setShowSupportChannelsModal(true)} 
                style={{ ...primaryBtn, width: 'fit-content' }}
              >
                Manage Support Channels
              </button>
            </div>

            {/* Save */}
            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
              <button onClick={handleExportConfig} disabled={saving} style={{ ...secondaryBtn, opacity: saving ? 0.6 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}>
                {saving ? '...' : '⬇ Export Config'}
              </button>
              {superAdmin && (
                <button onClick={handleSaveConfig} disabled={saving} style={{ ...primaryBtn, opacity: saving ? 0.6 : 1, cursor: saving ? 'not-allowed' : 'pointer' }}>
                  {saving ? 'Saving…' : '✓ Save Settings'}
                </button>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <SystemStatusDetailed />
            <RecentChanges />
          </div>
        </div>
      )}

      {/* ── Tab: Payment ── */}
      {activeTab === 'payment' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 24 }}>
          <PaymentGateway 
            configValues={configValues} 
            onChange={handleConfigChange} 
            onSave={handleSaveConfig} 
            saving={saving} 
            loading={loadingConfig}
            readOnly={!superAdmin}
          />
          <div style={cardStyle}>
            <h3 style={{ ...sectionTitle, marginBottom: 14 }}>Payment Notes</h3>
            <div style={{ fontSize: 13, color: 'hsl(var(--muted-foreground))', lineHeight: 1.7 }}>
              Changes to payment gateway settings take effect immediately. Always test the connection after making any key changes.
              For security, secret keys are masked after saving.
            </div>
          </div>
        </div>
      )}

      {/* ── Tab: Notifications ── */}
      {activeTab === 'notifications' && (
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 340px', gap: 24 }}>
          <NotificationPreferences 
            configValues={configValues} 
            onChange={handleConfigChange} 
            onSave={handleSaveConfig} 
            saving={saving} 
            loading={loadingConfig}
          />
          <div style={cardStyle}>
            <h3 style={{ ...sectionTitle, marginBottom: 14 }}>Notification Channels</h3>
            <div style={{ fontSize: 13, color: 'hsl(var(--muted-foreground))', lineHeight: 1.7 }}>
              Configure which channels are used for platform-level alerts. Changes affect all new notifications.
            </div>
          </div>
        </div>
      )}

      {/* ── Tab: System ── */}
      {activeTab === 'system' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <SystemStatusDetailed />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <RecentChanges />
            <div style={cardStyle}>
              <h3 style={{ ...sectionTitle, marginBottom: 14 }}>Danger Zone</h3>
              <div style={{ fontSize: 13, color: 'hsl(var(--muted-foreground))', lineHeight: 1.6, marginBottom: 16 }}>
                Destructive actions. These cannot be undone.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button
                  onClick={handleExportConfig}
                  style={{ ...secondaryBtn, textAlign: 'left' }}
                >
                  ⬇ Export Full Config Backup
                </button>
                {superAdmin && (
                  <button
                    style={{ ...secondaryBtn, color: '#b91c1c', borderColor: '#fecaca', textAlign: 'left' }}
                    onClick={() => showToast('Maintenance mode — contact your DevOps team to enable', 'error')}
                  >
                     Enable Maintenance Mode
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Scholar Support Channels Modal ── */}
      {showSupportChannelsModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'grid', placeItems: 'center', zIndex: 1000 }}>
          <div style={{ background: 'hsl(var(--card))', color: 'hsl(var(--foreground))', border: '1px solid hsl(var(--border))', borderRadius: '16px', width: '450px', padding: '24px', boxShadow: '0 10px 25px rgba(0,0,0,0.15)' }}>
            <h3 style={{ ...sectionTitle, marginBottom: 10 }}>Manage Support Channels</h3>
            <p style={{ fontSize: 13, color: 'hsl(var(--muted-foreground))', marginBottom: 20 }}>
              Configure the support options available to Scholars on their app.
            </p>

            {/* WhatsApp Support Section */}
            <div style={{ marginBottom: 20, padding: 14, borderRadius: 12, border: '1px solid hsl(var(--border))', background: 'hsl(var(--muted)/0.05)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontSize: 14, fontWeight: 700 }}>WhatsApp Support</span>
                <input
                  type="checkbox"
                  checked={(configValues['support_whatsapp_enabled'] || 'true') === 'true'}
                  onChange={(e) => handleConfigChange('support_whatsapp_enabled', e.target.checked ? 'true' : 'false')}
                  style={{ width: '18px', height: '18px', accentColor: 'hsl(var(--primary))', cursor: 'pointer' }}
                />
              </div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'hsl(var(--muted-foreground))', marginBottom: '6px' }}>WhatsApp Number / Link</label>
              <input
                type="text"
                value={configValues['support_whatsapp_number'] || ''}
                placeholder="+91 18001234567"
                onChange={(e) => handleConfigChange('support_whatsapp_number', e.target.value)}
                style={inputStyle}
                disabled={(configValues['support_whatsapp_enabled'] || 'true') !== 'true'}
              />
            </div>

            {/* Phone Support Section */}
            <div style={{ marginBottom: 20, padding: 14, borderRadius: 12, border: '1px solid hsl(var(--border))', background: 'hsl(var(--muted)/0.05)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontSize: 14, fontWeight: 700 }}>Phone Support</span>
                <input
                  type="checkbox"
                  checked={(configValues['support_phone_enabled'] || 'true') === 'true'}
                  onChange={(e) => handleConfigChange('support_phone_enabled', e.target.checked ? 'true' : 'false')}
                  style={{ width: '18px', height: '18px', accentColor: 'hsl(var(--primary))', cursor: 'pointer' }}
                />
              </div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 600, color: 'hsl(var(--muted-foreground))', marginBottom: '6px' }}>Support Phone Number</label>
              <input
                type="text"
                value={configValues['support_phone_number'] || ''}
                placeholder="+91 9916877128"
                onChange={(e) => handleConfigChange('support_phone_number', e.target.value)}
                style={inputStyle}
                disabled={(configValues['support_phone_enabled'] || 'true') !== 'true'}
              />
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 10 }}>
              <button
                onClick={() => setShowSupportChannelsModal(false)}
                style={secondaryBtn}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  await handleSaveConfig();
                  setShowSupportChannelsModal(false);
                }}
                disabled={saving}
                style={primaryBtn}
              >
                {saving ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
